/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { BuilderPermission, SystemRoleBit } from '@gh/builder-shared-data';
import {
  FormStateSnapshot,
  getFormStateSnapshotValue,
  getInvalidFields,
  hasFormStateError,
  isEntityInvalid,
  isRouteForCreationMode,
  markFormStateAsTouched,
  MutablePageStateWithSelection,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { FormMode } from '@gh/core-ui';
import { isNotNil } from '@gh/core-util';
import { first, last, pull } from 'lodash';
import {
  ChecklistDetails,
  ChklClause,
  ChklLevel,
  ChklMasterInfo,
  ChklSection,
  ChklSectionInfo,
  ChklSelection,
  chklTreeHelper,
  getChklClauseId,
  hasChklElementError,
  hasChklSectionInvalidItem,
  isChklSection,
  validateChklMasterInfo,
} from '../../resources/checklists.model';
import { FocusedCell } from '@gh/core-ui-grid';
import { BuilderUserContext } from '@gh/builder-shared';

@Injectable()
export class ChecklistsDetailFormService extends MutablePageStateWithSelection<ChecklistDetails, ChklSelection>({}) {
  currentElementType: string | undefined;
  constructor(private route: ActivatedRoute,
              private userContext: BuilderUserContext) {
    super();
  }

  get id(): Maybe<ID> {
    return this.value.id;
  }
  get invalid(): boolean {
    // check validity of top form
    const { masterInfo } = this.value;
    if (hasFormStateError(masterInfo) || isNotNil(validateChklMasterInfo(getFormStateSnapshotValue(masterInfo)))) {
      return true;
    }

    // check validity of tree content
    return chklTreeHelper.reduceNodes(
      this.value.checklistElements,
      (invalid: boolean, element: ChklSection) =>
        invalid || hasChklElementError(element),
      false);
  }

  highlightAll(): void {
    // highlight top form
    const { masterInfo } = this.value;
    if (hasFormStateError(masterInfo) || validateChklMasterInfo(getFormStateSnapshotValue(masterInfo))) {
      this.value.masterInfo = markFormStateAsTouched(masterInfo);
      this.emitChange();
      return;
    }

    // find the first invalid tree node
    const invalidPath = chklTreeHelper.reduceNodes(
      this.value.checklistElements,
      (invalid, element: ChklSection, _1, _2, path) => {
        if (invalid) {
          return invalid;
        }

        return hasChklElementError(element) ? path : void 0;
      },
      <Maybe<any[]>>void 0);

    if (invalidPath) {
      const lastNode = last(invalidPath)!; // tslint:disable-line:no-non-null-assertion
      this.selectByPath(invalidPath);
      if (hasChklElementError(lastNode)) {
        lastNode.info = markFormStateAsTouched(lastNode.info);
      } else if (isChklSection(lastNode) && hasChklSectionInvalidItem(lastNode)) {
        // tslint:disable-next-line:no-non-null-assertion
        this.focusInvalidItem(first(lastNode.checklistItems.filter(isEntityInvalid))!);
      }
      this.emitChange();
    }
  }

  build(details: ChecklistDetails): void {
    let mode: FormMode;
    if (isRouteForCreationMode(this.route)) {
      mode = FormMode.CreateMode;
    } else if (this.userContext.corporateView ||
      (
        (this.userContext.hasPermission(BuilderPermission.Setup.Checklists.EDIT) || this.userContext.hasSystemRole(SystemRoleBit.Master)) &&
        (!details.masterInfo.value.notEditable || details.isParent || details.isParentEditable)
      )
    ) {
      mode = FormMode.EditMode;
    } else {
      mode = FormMode.ViewMode;
    }
    this.setMode(mode);
    this.setValue(details);

    this.selectFirstSection();
  }

  merge(details: ChecklistDetails): void {
    chklTreeHelper.merge(this.value.checklistElements, details.checklistElements, (thisNode, anotherNode, level) => {
      switch (level) {
        case ChklLevel.Section:
          thisNode.isNew = false;
          thisNode.elementId = anotherNode.elementId;
          break;
        default:
          break;
      }
    });

    // this.value.version = details.version;
    this.emitChange();
  }

  getCurrentElementType(): Maybe<string> {
    return this.currentElementType;
  }
  updateMasterInfo(masterInfo: FormStateSnapshot<ChklMasterInfo>): void {
    this.value.masterInfo = masterInfo;
    this.setDirty();
    this.emitChange();
  }
  updateSectionInfo(section: ChklSection, sectionInfo: FormStateSnapshot<ChklSectionInfo>): void {
    section.info = sectionInfo;
    this.currentElementType = sectionInfo.value.elementType;
    this.setDirty();
    this.emitChange();
  }
  updateSectionClauses(section: ChklSection, clauses: ChklClause[]): void {
    section.checklistItems = clauses;
    this.setDirty();
    this.emitChange();
  }
  addSection(section: ChklSection): void {
    this.value.checklistElements.push(section);
    this.setDirty();
    this.emitChange();
  }
  removeSection(section: ChklSection): void {
    this.ensureExistingNodeSelected([section]);

    pull(this.value.checklistElements, section);
    this.setDirty();
    this.emitChange();
  }

  selectSection(section: ChklSection): void {
    this.setSelection({
      level: ChklLevel.Section,
      section,
      clauseFocusedCell: void 0,
    });
    this.currentElementType = section.info.value.elementType;
  }

  resetSelection(): void {
    this.setSelection({
      level: void 0,
      section: void 0,
      clauseFocusedCell: void 0,
    });
  }

  selectByPath(path: any[]): void {
    switch (path.length) {
      case ChklLevel.Section:
        this.selectSection(path[0]);
        break;
      default:
        this.resetSelection();
        break;
    }
  }
  resetFocusedCell(): void {
    this.focusClauseCell(void 0);
  }
  moveNodes(): void {
    this.setDirty();
    this.emitChange();
  }

  private selectFirstSection(): void {
    const firstSection = first(this.value.checklistElements);
    if (firstSection) {
      this.selectSection(firstSection);
    }
  }
  private ensureExistingNodeSelected(removedPath: any[]): void {
    const { selection } = this;
    const path = chklTreeHelper.findNextNode(
      this.value.checklistElements,
      removedPath,
      chklTreeHelper.createPath(selection.section));

    this.selectByPath(path);
  }
  private focusClauseCell(clauseFocusedCell?: FocusedCell): void {
    this.setSelection({
                        ...this.selection,
                        clauseFocusedCell,
                      });
  }

  private focusInvalidItem(item: ChklClause): void {
    const invalidFields = getInvalidFields(item);
    const firstInvalidField = first(invalidFields);
    if (firstInvalidField) {
      this.focusClauseCell({ id: getChklClauseId(item), field: firstInvalidField });
    }
  }
}
